/**
 * Copyright Higharc Inc. 2019
 *
 * GLSL code with utilities for dealing with light probe grids regardless of the format of the light probes.
 * @module
 */
import { cubeMapGridHelpers } from "./cubeMapGrid";

const lightProbeGridHelpers = `
#ifndef LIGHTPROBE_WEIGHTING_FUNCTION
// Another alternative to this is getProbeTrilinearWeight.
#define LIGHTPROBE_WEIGHTING_FUNCTION getProbeWeightWithSmoothBackface
#endif

int lightProbeIndex( ivec3 gridCoordinates, ivec3 probeCount ) {

	return gridCoordinates.x * probeCount.y * probeCount.z + gridCoordinates.y * probeCount.z + gridCoordinates.z;

}

vec3 clampCoordinatesToGrid( vec3 gridCoordinates, ivec3 probeCount ) {

	return max( min( gridCoordinates, vec3( probeCount ) - 1.0 ), 0.0 );

}

// This function takes the worldNormal parameter so that it's interchangeable with getProbeWeightWithSmoothBackface
// Both gridCoordinates and probeCoordinates are in grid space.
float getProbeTrilinearWeight( vec3 gridCoordinates, vec3 probeCoordinates, vec3 worldNormal ) {
	vec3 diff = abs( gridCoordinates - probeCoordinates );
	return (1.0 - diff.x) * (1.0 - diff.y) * (1.0 - diff.z);
}

// Both gridCoordinates and probeCoordinates are in grid space.
float getProbeWeightWithSmoothBackface( vec3 gridCoordinates, vec3 probeCoordinates, vec3 worldNormal ) {
	vec3 diff = abs( gridCoordinates - probeCoordinates );
	float trilinearWeight = (1.0 - diff.x) * (1.0 - diff.y) * (1.0 - diff.z) + 0.001;

	// The smooth backface term is similar to one used in https://www.gdcvault.com/play/1026182/
	// Ray-Traced Irradiance Fields (NVIDIA)
	float smoothBackface = pow( dot ( normalize( probeCoordinates - gridCoordinates ), worldNormal ) + 1.0, 2.0 ) + probeGridSmoothBackfaceWeightConstantTerm;
	return trilinearWeight * smoothBackface;
}

// Pick a random cube corner from 8 corners with arbitrary weights.
// The cube corners are identified by vec3s with all values set to either 0 or 1.
// The weights array is ordered so that the weight for corner (0, 0, 0) comes first, followed by weights for:
// (0, 0, 1) (0, 1, 0), (0, 1, 1), (1, 0, 0), (1, 0, 1) (1, 1, 0), (1, 1, 1)
// The randomValue parameter should be evenly distributed between 0 and 1.
vec3 weighedRandomCubeCorner( float cornerWeights[8], float randomValue ) {
	float weightTotal = 0.0;
	for ( int i = 0; i < 8; ++i ) {
		weightTotal += cornerWeights[i];
	}

	float cumulativeWeight = 0.0;
	vec3 randomPick = vec3( 1.0 );
	for ( int i = 0; i < 8; ++i ) {
		cumulativeWeight += cornerWeights[i];
		if ( cumulativeWeight > randomValue * weightTotal ) {
			// The below statement is equivalent to randomPick = vec3( i & 4, i & 2, i & 1 )
			randomPick = vec3 ( i > 3, i == 2 || i == 3 || i == 6 || i == 7, i == 1 || i == 3 || i == 5 || i == 7 );
			break;
		}
	}
	return randomPick;
}
`;

export const lightProbeGridParsFragment = `#if defined(LIGHT_PROBES_ENABLED) && LIGHT_PROBES_ENABLED != 0

uniform float probeGridNormalOffsetMultiplier;
uniform float probeGridSmoothBackfaceWeightConstantTerm;

${lightProbeGridHelpers}
${cubeMapGridHelpers}

// Reference: https://en.wikibooks.org/wiki/GLSL_Programming/Applying_Matrix_Transformations
// This can be used for inverse transformations with certain restrictions.
vec3 inverseTransformPosition( vec3 pos, mat4 matrix ) {
	return vec3( ( vec4( pos, 1.0 ) - matrix[3] ) * matrix );
}

uniform sampler2D lightProbeData;
uniform ivec3 lightProbeCount;
uniform mat4 worldToLightProbeGrid;
uniform int lightProbeCubeResolution;

uniform float lightProbeIrradianceMultiplier;

vec3 lightProbeIrradiance;

#endif`;

// This is intended to replace the "lights_fragment_maps" shader chunk.
export const lightProbeGridAndMapsFragment = `
#if defined( RE_IndirectDiffuse )
  #ifdef USE_LIGHTMAP
    vec3 lightMapIrradiance = texture2D( lightMap, vUv2 ).xyz * lightMapIntensity;
    #ifndef PHYSICALLY_CORRECT_LIGHTS
      lightMapIrradiance *= PI; // factor of PI should not be present; included here to prevent breakage
    #endif
    irradiance += lightMapIrradiance;
  #endif

  #if defined(LIGHT_PROBES_ENABLED) && LIGHT_PROBES_ENABLED != 0
  {
    vec2 screenSpaceCoords = gl_FragCoord.xy / renderTargetResolution;
    vec3 randomJitter = vec3(rand(screenSpaceCoords + randomSeed + 0.25), rand(screenSpaceCoords + randomSeed + 0.5), rand(screenSpaceCoords + randomSeed + 0.75));
    vec3 worldNormal = transformDirectionByInverseViewMatrix( geometryNormal, viewMatrix );
    vec3 cubeSamplingDirection = normalize( ( worldToLightProbeGrid * vec4( worldNormal, 0.0 ) ).xyz ) + randomJitter / float(lightProbeCubeResolution);
    vec4 sampledIrradiance = sampleInterleavedCubeMapAtlas( lightProbeData, vWorldPosition.xyz, worldNormal, screenSpaceAo, lightProbeCount, worldToLightProbeGrid, lightProbeCubeResolution, cubeSamplingDirection );
    // TODO: Sample multiple directions from the lightprobe? Vary sampling based on occlusion?
    // Multiply by alpha * 10.0 to convert from RGBM format.
    lightProbeIrradiance = sampledIrradiance.rgb * (sampledIrradiance.a * 10.0) * lightProbeIrradianceMultiplier;
    // Three.js divides the irradiance by pi (In both BRDF_Lambert function (used for irradiance) and RE_IndirectSpecular_Physical (used for iblIrradiance)).
    lightProbeIrradiance *= PI;
    iblIrradiance += lightProbeIrradiance;
  }
  #elif defined( USE_ENVMAP ) && defined( STANDARD ) && defined( ENVMAP_TYPE_CUBE_UV )
    iblIrradiance += getIBLIrradiance( geometryNormal );
  #endif
#endif

#if defined(SSR) && SSR == 1
  {
    vec4 reflection = reflectionTexelToLinear( texture2D( screenSpaceReflectionMap, gl_FragCoord.xy / renderTargetResolution ) );
    #if defined(SUBSTITUTE_MISSING_REFLECTION_WITH_IRRADIANCE) && SUBSTITUTE_MISSING_REFLECTION_WITH_IRRADIANCE == 1
      #if defined(LIGHT_PROBES_ENABLED) && LIGHT_PROBES_ENABLED != 0
        #define ENV_RADIANCE lightProbeIrradiance * RECIPROCAL_PI
      #else
        #define ENV_RADIANCE irradiance * RECIPROCAL_PI
      #endif
      radiance += ssfxEnabled ? mix( ENV_RADIANCE * substitutedReflectionMultiplier, reflection.rgb, reflection.a ) : vec3(0.0);
    #else // Substitute missing reflection with black
      radiance += ssfxEnabled ? reflection.rgb * reflection.a : vec3(0.0);
    #endif
    #ifdef USE_CLEARCOAT
      clearcoatRadiance = radiance;
    #endif
  }
#else
  #if defined( USE_ENVMAP ) && defined( RE_IndirectSpecular )
    radiance += getIBLRadiance( geometryViewDir, geometryNormal, material.roughness );
    #ifdef USE_CLEARCOAT
      clearcoatRadiance += getIBLRadiance( geometryViewDir, geometryClearcoatNormal, material.clearcoatRoughness );
    #endif
  #endif
#endif
`;
